import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import {
  onIndividualAuthorizations, createAuthorization, updateAuthorization,
  deleteAuthorization, GEORGIA_PAYERS, AUTH_REQUIRED_SERVICES,
} from "../services/authorizationService";
import {
  Plus, X, AlertTriangle, Clock, Check, Shield, ChevronDown, ChevronUp, Trash2,
} from "lucide-react";

const S = {
  card: {
    background: "rgba(15,23,42,0.7)",
    border: "1px solid rgba(99,179,237,0.08)",
    borderRadius: 14,
    padding: 16,
    marginBottom: 12,
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    background: "rgba(15,23,42,0.5)",
    border: "1px solid rgba(99,179,237,0.12)",
    color: "#e2e8f0",
    fontSize: 13,
    outline: "none",
  },
  btn: {
    padding: "8px 16px",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    border: "none",
  },
  label: {
    fontSize: 10,
    fontWeight: 700,
    color: "#718096",
    textTransform: "uppercase",
    letterSpacing: 1,
    marginBottom: 4,
    display: "block",
  },
};

function ProgressBar({ used, approved, color }) {
  const pct = approved > 0 ? Math.min(100, (used / approved) * 100) : 0;
  const barColor = pct >= 90 ? "#fc8181" : pct >= 80 ? "#f6ad55" : color || "#4fd1c5";
  return (
    <div style={{ width: "100%", height: 6, borderRadius: 3, background: "rgba(99,179,237,0.06)", overflow: "hidden" }}>
      <div style={{
        width: `${pct}%`, height: "100%", borderRadius: 3,
        background: barColor,
        transition: "width 0.3s ease",
      }} />
    </div>
  );
}

function AuthCard({ auth, canEdit, onDelete }) {
  const remaining = Math.max(0, auth.unitsApproved - auth.unitsUsed);
  const pct = auth.unitsApproved > 0 ? (auth.unitsUsed / auth.unitsApproved) * 100 : 0;
  const today = new Date().toISOString().split("T")[0];

  // Days until expiration
  let daysLeft = null;
  if (auth.endDate) {
    const end = new Date(auth.endDate);
    const now = new Date();
    daysLeft = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  }

  const isExpired = auth.status === "expired" || (auth.endDate && auth.endDate < today);
  const isExhausted = auth.status === "exhausted" || (auth.unitsApproved > 0 && auth.unitsUsed >= auth.unitsApproved);
  const isWarning = !isExpired && !isExhausted && (pct >= 80 || (daysLeft !== null && daysLeft <= 30));

  const statusColor = isExpired ? "#fc8181" : isExhausted ? "#f6ad55" : isWarning ? "#ecc94b" : "#68d391";
  const statusLabel = isExpired ? "Expired" : isExhausted ? "Exhausted" : auth.status === "pending" ? "Pending" : "Active";

  return (
    <div style={{
      ...S.card,
      borderLeft: `3px solid ${statusColor}`,
      opacity: isExpired ? 0.6 : 1,
    }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 800 }}>{auth.serviceType || auth.billingCode}</span>
            <span style={{ fontSize: 10, fontWeight: 700, fontFamily: "monospace", color: "#63b3ed" }}>{auth.billingCode}</span>
          </div>
          <div style={{ fontSize: 11, color: "#a0aec0", marginTop: 2 }}>
            {auth.payerName || "—"}
            {auth.authNumber && <span style={{ marginLeft: 8, fontFamily: "monospace", color: "#718096" }}>#{auth.authNumber}</span>}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{
            fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 6, textTransform: "uppercase",
            background: `${statusColor}15`, color: statusColor,
          }}>{statusLabel}</span>
          {canEdit && (
            <button onClick={() => {
              if (window.confirm("Delete this authorization?")) onDelete(auth.id);
            }} style={{ background: "none", border: "none", cursor: "pointer", color: "#4a5568", padding: 2 }}>
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </div>

      {/* Dates */}
      <div style={{ display: "flex", gap: 16, marginTop: 10, fontSize: 10, color: "#718096" }}>
        <span>Start: {auth.startDate || "—"}</span>
        <span>End: {auth.endDate || "N/A"}</span>
        {daysLeft !== null && daysLeft > 0 && !isExpired && (
          <span style={{ color: daysLeft <= 14 ? "#fc8181" : daysLeft <= 30 ? "#ecc94b" : "#68d391", fontWeight: 700 }}>
            {daysLeft} days left
          </span>
        )}
      </div>

      {/* Units progress */}
      <div style={{ marginTop: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: "#a0aec0" }}>
            {auth.unitsUsed} / {auth.unitsApproved} {auth.unitType === "encounter" ? "encounters" : "units"} used
          </span>
          <span style={{ fontSize: 10, fontWeight: 800, color: statusColor }}>
            {remaining} remaining
          </span>
        </div>
        <ProgressBar used={auth.unitsUsed} approved={auth.unitsApproved} color={statusColor} />
        <div style={{ fontSize: 9, color: "#4a5568", marginTop: 2, textAlign: "right" }}>
          {Math.round(pct)}% utilized
        </div>
      </div>

      {/* Warnings */}
      {isWarning && (
        <div style={{
          marginTop: 8, padding: "6px 10px", borderRadius: 8,
          background: "rgba(236,201,75,0.04)", border: "1px solid rgba(236,201,75,0.1)",
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <AlertTriangle size={11} style={{ color: "#ecc94b" }} />
          <span style={{ fontSize: 10, color: "#ecc94b", fontWeight: 600 }}>
            {pct >= 80 ? `${Math.round(pct)}% of units used — request reauthorization soon` : ""}
            {pct >= 80 && daysLeft !== null && daysLeft <= 30 ? " · " : ""}
            {daysLeft !== null && daysLeft <= 30 ? `Expiring in ${daysLeft} days` : ""}
          </span>
        </div>
      )}

      {/* Notes */}
      {auth.notes && (
        <div style={{ fontSize: 10, color: "#4a5568", marginTop: 6, fontStyle: "italic" }}>
          {auth.notes}
        </div>
      )}
    </div>
  );
}


export default function AuthorizationTab({ individual }) {
  const { profile } = useAuth();
  const role = profile?.role || "agent";
  const canEdit = ["supervisor", "admin", "billing"].includes(role);

  const [auths, setAuths] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showExpired, setShowExpired] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({
    serviceType: "",
    billingCode: "",
    payerName: "Georgia Medicaid (DCH)",
    authNumber: "",
    unitsApproved: "",
    unitType: "15-min",
    startDate: new Date().toISOString().split("T")[0],
    endDate: "",
    notes: "",
  });

  // Subscribe to authorizations
  useEffect(() => {
    if (!individual?.id) return;
    return onIndividualAuthorizations(individual.id, setAuths);
  }, [individual?.id]);

  const active = auths.filter(a => a.status === "active" || a.status === "pending");
  const warning = auths.filter(a => {
    if (a.status !== "active") return false;
    const pct = a.unitsApproved > 0 ? (a.unitsUsed / a.unitsApproved) * 100 : 0;
    const daysLeft = a.endDate ? Math.ceil((new Date(a.endDate) - new Date()) / 86400000) : null;
    return pct >= 80 || (daysLeft !== null && daysLeft <= 30);
  });
  const exhaustedOrExpired = auths.filter(a => a.status === "expired" || a.status === "exhausted");

  const handleServiceSelect = (code) => {
    const svc = AUTH_REQUIRED_SERVICES.find(s => s.code === code);
    if (svc) {
      setForm(f => ({
        ...f,
        billingCode: svc.code,
        serviceType: svc.label,
        unitType: svc.unitType,
        unitsApproved: String(svc.typicalUnits),
      }));
    }
  };

  const handleAdd = async () => {
    if (!form.billingCode || !form.unitsApproved) return;
    setSaving(true);
    try {
      await createAuthorization({
        individualId: individual.id,
        ...form,
        unitsApproved: parseInt(form.unitsApproved) || 0,
        createdBy: profile?.displayName || "Unknown",
      });
      setShowAdd(false);
      setForm({
        serviceType: "", billingCode: "", payerName: "Georgia Medicaid (DCH)",
        authNumber: "", unitsApproved: "", unitType: "15-min",
        startDate: new Date().toISOString().split("T")[0], endDate: "", notes: "",
      });
    } catch (e) {
      console.error("Failed to create auth:", e);
    }
    setSaving(false);
  };

  const handleDelete = async (authId) => {
    try {
      await deleteAuthorization(authId);
    } catch (e) {
      console.error("Failed to delete auth:", e);
    }
  };

  if (!individual) return null;

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800 }}>Authorizations</div>
          <div style={{ fontSize: 10, color: "#718096" }}>
            {active.length} active{warning.length > 0 && ` · ${warning.length} need attention`}
          </div>
        </div>
        {canEdit && (
          <button onClick={() => setShowAdd(!showAdd)} style={{
            ...S.btn,
            background: showAdd ? "rgba(252,129,129,0.1)" : "rgba(79,209,197,0.1)",
            color: showAdd ? "#fc8181" : "#4fd1c5",
          }}>
            {showAdd ? <><X size={12} /> Cancel</> : <><Plus size={12} /> Add Auth</>}
          </button>
        )}
      </div>

      {!canEdit && (
        <div style={{
          padding: "8px 12px", borderRadius: 10, marginBottom: 12,
          background: "rgba(236,201,75,0.04)", border: "1px solid rgba(236,201,75,0.1)",
          fontSize: 11, color: "#ecc94b",
        }}>
          Read-only view. Supervisors and billing staff can manage authorizations.
        </div>
      )}

      {/* Add Form */}
      {showAdd && (
        <div style={{ ...S.card, background: "rgba(79,209,197,0.03)", border: "1px solid rgba(79,209,197,0.12)" }}>
          <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 12 }}>New Authorization</div>

          {/* Service type selector */}
          <div style={{ marginBottom: 10 }}>
            <div style={S.label}>Service Type</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {AUTH_REQUIRED_SERVICES.map(svc => (
                <button key={svc.code} onClick={() => handleServiceSelect(svc.code)} style={{
                  ...S.btn, fontSize: 10, padding: "6px 10px",
                  background: form.billingCode === svc.code ? "rgba(79,209,197,0.15)" : "rgba(15,23,42,0.5)",
                  color: form.billingCode === svc.code ? "#4fd1c5" : "#718096",
                  border: `1px solid ${form.billingCode === svc.code ? "rgba(79,209,197,0.3)" : "rgba(99,179,237,0.06)"}`,
                }}>
                  {svc.label} ({svc.code})
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <div>
              <div style={S.label}>Payer</div>
              <select style={{ ...S.input, appearance: "auto" }} value={form.payerName}
                onChange={e => setForm(f => ({ ...f, payerName: e.target.value }))}>
                {GEORGIA_PAYERS.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <div style={S.label}>Auth Number</div>
              <input style={S.input} placeholder="e.g., AUTH-2025-001" value={form.authNumber}
                onChange={e => setForm(f => ({ ...f, authNumber: e.target.value }))} />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
            <div>
              <div style={S.label}>Units Approved</div>
              <input style={S.input} type="number" placeholder="480" value={form.unitsApproved}
                onChange={e => setForm(f => ({ ...f, unitsApproved: e.target.value }))} />
            </div>
            <div>
              <div style={S.label}>Start Date</div>
              <input style={S.input} type="date" value={form.startDate}
                onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} />
            </div>
            <div>
              <div style={S.label}>End Date</div>
              <input style={S.input} type="date" value={form.endDate}
                onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} />
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <div style={S.label}>Notes</div>
            <input style={S.input} placeholder="Optional notes..." value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>

          <button onClick={handleAdd} disabled={saving || !form.billingCode || !form.unitsApproved} style={{
            ...S.btn, width: "100%", padding: 12,
            background: form.billingCode && form.unitsApproved ? "linear-gradient(135deg, #319795, #2c7a7b)" : "rgba(79,209,197,0.1)",
            color: form.billingCode && form.unitsApproved ? "white" : "#718096",
            opacity: saving ? 0.5 : 1,
          }}>
            {saving ? "Creating..." : "Create Authorization"}
          </button>
        </div>
      )}

      {/* Summary stats */}
      {auths.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 16 }}>
          {[
            { label: "Active", value: active.length, color: "#68d391" },
            { label: "Attention", value: warning.length, color: warning.length > 0 ? "#ecc94b" : "#68d391" },
            { label: "Expired", value: exhaustedOrExpired.length, color: exhaustedOrExpired.length > 0 ? "#fc8181" : "#4a5568" },
          ].map((s, i) => (
            <div key={i} style={{ ...S.card, textAlign: "center", padding: 10, marginBottom: 0 }}>
              <div style={{ fontSize: 20, fontWeight: 900, color: s.color, fontFamily: "monospace" }}>{s.value}</div>
              <div style={{ fontSize: 8, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Active authorizations */}
      {active.length === 0 && !showAdd ? (
        <div style={{ textAlign: "center", padding: "32px 0" }}>
          <Shield size={28} style={{ color: "#2d3748", margin: "0 auto 8px" }} />
          <div style={{ fontSize: 13, color: "#4a5568" }}>No active authorizations</div>
          <div style={{ fontSize: 11, color: "#2d3748", marginTop: 4 }}>
            {canEdit ? "Add an authorization to track approved units." : "Supervisor or billing staff can add authorizations."}
          </div>
        </div>
      ) : (
        active.map(auth => (
          <AuthCard key={auth.id} auth={auth} canEdit={canEdit} onDelete={handleDelete} />
        ))
      )}

      {/* Expired / Exhausted */}
      {exhaustedOrExpired.length > 0 && (
        <>
          <button onClick={() => setShowExpired(!showExpired)} style={{
            ...S.btn, width: "100%", marginTop: 4, background: "rgba(15,23,42,0.3)",
            color: "#4a5568", fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
          }}>
            {showExpired ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            Expired / Exhausted ({exhaustedOrExpired.length})
          </button>
          {showExpired && exhaustedOrExpired.map(auth => (
            <AuthCard key={auth.id} auth={auth} canEdit={canEdit} onDelete={handleDelete} />
          ))}
        </>
      )}
    </div>
  );
}